import { useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  diffWords,
  DIFF_SEGMENT_ADD,
  DIFF_SEGMENT_REMOVE,
} from "../../../../utils/textDiff";
import {
  CREDITS_USAGE_LOW_BG_COLOR,
  CREDITS_USAGE_LOW_TEXT_COLOR,
  CREDITS_USAGE_HIGH_BG_COLOR,
  CREDITS_USAGE_HIGH_TEXT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";

const SEGMENT_STYLES = {
  [DIFF_SEGMENT_ADD]: {
    backgroundColor: CREDITS_USAGE_LOW_BG_COLOR,
    color: CREDITS_USAGE_LOW_TEXT_COLOR,
    fontWeight: 600,
    borderRadius: "3px",
  },
  [DIFF_SEGMENT_REMOVE]: {
    backgroundColor: CREDITS_USAGE_HIGH_BG_COLOR,
    color: CREDITS_USAGE_HIGH_TEXT_COLOR,
    textDecoration: "line-through",
    borderRadius: "3px",
  },
};

const scrollChangeIntoView = (container, target) => {
  if (!container || !target) {
    return;
  }
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  container.scrollTop += targetRect.top - containerRect.top - 16;
};

/**
 * Full field after the edit: untouched text stays neutral, additions are
 * highlighted and removals struck through. The first addition (or removal)
 * is scrolled into view so the proposed change stays visible.
 */
const ProposalDiff = ({ currentValue, proposedValue }) => {
  const containerRef = useRef(null);
  const firstChangeRef = useRef(null);

  const segments = useMemo(
    () => diffWords(currentValue, proposedValue),
    [currentValue, proposedValue]
  );

  const firstChangeIndex = useMemo(() => {
    const addedIndex = segments.findIndex(
      (segment) => segment.type === DIFF_SEGMENT_ADD
    );
    if (addedIndex >= 0) {
      return addedIndex;
    }
    return segments.findIndex(
      (segment) => segment.type === DIFF_SEGMENT_REMOVE
    );
  }, [segments]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      scrollChangeIntoView(containerRef.current, firstChangeRef.current);
    });
    return () => cancelAnimationFrame(frame);
  }, [segments]);

  return (
    <Box
      ref={containerRef}
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        backgroundColor: "background.paper",
        maxHeight: 320,
        overflowY: "auto",
      }}
    >
      <Typography
        variant="body2"
        component="p"
        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
      >
        {segments.map((segment, index) => (
          <Box
            key={`segment-${index}`}
            ref={index === firstChangeIndex ? firstChangeRef : undefined}
            component="span"
            sx={SEGMENT_STYLES[segment.type] || { color: "text.secondary" }}
          >
            {segment.text}
          </Box>
        ))}
      </Typography>
    </Box>
  );
};

export default ProposalDiff;
